import { forwardRef } from "react";
import { twMerge } from "tailwind-merge";

import { BaseButton, type BaseButtonProps } from "./BaseButton";
import { BUTTON_BASE_CLASSES, BUTTON_DISABLED_WITHOUT_BORDER_CLASSES } from "./helpers";

import { BaseSizeType } from "mds/global-types";

type LinkButtonSize = Extract<BaseSizeType, "xs" | "s" | "m">;

// NOTE: [&>*]:!gap-x is needed to override any gap class that might be applied by the child component, such as IconText.
// Similarly, [&_svg]:!size-x is needed to override any size class that might be applied by a child icon.
const SIZE_CLASSES: Record<LinkButtonSize, string> = {
  xs: "text-xs gap-1.5 [&>*]:!gap-1.5 [&_svg]:!size-3",
  s: "text-sm gap-2 [&>*]:!gap-2 [&_svg]:!size-3.5",
  m: "text-base gap-2 [&>*]:!gap-2 [&_svg]:!size-4",
};

export type LinkButtonProps<T extends React.ElementType = "button"> = BaseButtonProps<T> & {
  size: LinkButtonSize;
};

/**
 * `LinkButton` is a button that looks like a text link.
 * Unlike other children of BaseButton, it is *not* a polymorphic component:
 * it can *only* be rendered as a <button> element.
 * If you need a text link that is not a button, use the `Link` component from react-router-dom.
 */
export const LinkButton = forwardRef<HTMLButtonElement, Omit<LinkButtonProps<"button">, "ref">>(
  function LinkButtonInternal({ size, className, ...rest }, ref) {
    return (
      <BaseButton
        as="button"
        className={twMerge(
          BUTTON_BASE_CLASSES,
          BUTTON_DISABLED_WITHOUT_BORDER_CLASSES,
          SIZE_CLASSES[size],
          "text-blue-shade-20 underline hover:text-blue-tint-20 disabled:bg-transparent",
          className,
        )}
        ref={ref}
        {...rest}
      />
    );
  },
);
